/**
 * Channel Conversation Types
 *
 * Types for multi-stage conversation flow across channels (email, etc.)
 *
 * Flow stages:
 * 1. TRIAGE - Identify if message is eligible for processing
 * 2. PROCESSING - Extract data from documents, may ask for missing info
 * 3. BILL - Create bill, notify success/error
 */

import { z } from 'zod';

// =============================================================================
// Conversation Stage Enum
// =============================================================================

/** Conversation stages in the flow */
export const ConversationStage = {
  TRIAGE: 'TRIAGE',
  PROCESSING: 'PROCESSING',
  BILL: 'BILL',
  APPROVAL: 'APPROVAL',
} as const;

export type ConversationStage = (typeof ConversationStage)[keyof typeof ConversationStage];

// =============================================================================
// Message Types
// =============================================================================

export const MessageType = {
  /** Acknowledgment - "Recebi seu email..." */
  ACK: 'ACK',
  /** Question - "Qual o valor total?" */
  QUESTION: 'QUESTION',
  /** Error notification - "Erro ao processar..." */
  ERROR: 'ERROR',
  /** Success notification - "Conta criada!" */
  SUCCESS: 'SUCCESS',
  /** Info - General information */
  INFO: 'INFO',
} as const;

export type MessageType = (typeof MessageType)[keyof typeof MessageType];

// =============================================================================
// Processing Error
// =============================================================================

/** Processing error stages (more granular than ConversationStage) */
export const ProcessingErrorStage = {
  TRIAGE: 'TRIAGE',
  EXTRACTION: 'EXTRACTION',
  BILL_CREATION: 'BILL_CREATION',
} as const;

export type ProcessingErrorStage = (typeof ProcessingErrorStage)[keyof typeof ProcessingErrorStage];

/** Processing error types */
export const ProcessingErrorType = {
  VALIDATION: 'VALIDATION',
  TIMEOUT: 'TIMEOUT',
  SYSTEM: 'SYSTEM',
  USER_INPUT_NEEDED: 'USER_INPUT_NEEDED',
  DOCUMENT_UNREADABLE: 'DOCUMENT_UNREADABLE',
  DUPLICATE: 'DUPLICATE',
} as const;

export type ProcessingErrorType = (typeof ProcessingErrorType)[keyof typeof ProcessingErrorType];

/** Extraction notification stages */
export const ExtractionNotificationStage = {
  STARTED: 'STARTED',
  MISSING_INFO: 'MISSING_INFO',
  ERROR: 'ERROR',
  COMPLETED: 'COMPLETED',
} as const;

export type ExtractionNotificationStage =
  (typeof ExtractionNotificationStage)[keyof typeof ExtractionNotificationStage];

/** Bill notification result types */
export const BillNotificationResult = {
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  NEEDS_REVIEW: 'NEEDS_REVIEW',
} as const;

export type BillNotificationResult =
  (typeof BillNotificationResult)[keyof typeof BillNotificationResult];

/** Conversation turn roles */
export const ConversationRole = {
  USER: 'user',
  ASSISTANT: 'assistant',
  SYSTEM: 'system',
} as const;

export type ConversationRole = (typeof ConversationRole)[keyof typeof ConversationRole];

// =============================================================================
// Zod Schemas - Derived from enums
// =============================================================================

export const ProcessingErrorSchema = z.object({
  stage: z.nativeEnum(ProcessingErrorStage),
  errorType: z.nativeEnum(ProcessingErrorType),
  message: z.string(),
  suggestedAction: z.string().optional(),
  technicalDetails: z.string().optional(),
});

export type ProcessingError = z.infer<typeof ProcessingErrorSchema>;

// =============================================================================
// Missing Field
// =============================================================================

export const MissingFieldSchema = z.object({
  /** Field type for categorization (e.g., 'vendor', 'amount', 'dueDate') */
  fieldType: z.string(),
  /** Display name of the field */
  fieldName: z.string(),
  /** Friendly question to ask the user */
  question: z.string(),
  /** Whether this field is required to proceed */
  required: z.boolean(),
  /** Example value for the user */
  example: z.string().optional(),
});

export type MissingField = z.infer<typeof MissingFieldSchema>;

export const ConversationTurnSchema = z.object({
  role: z.nativeEnum(ConversationRole),
  content: z.string(),
  timestamp: z.date(),
  stage: z.nativeEnum(ConversationStage),
  messageType: z.nativeEnum(MessageType).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export type ConversationTurn = z.infer<typeof ConversationTurnSchema>;

export const ConversationStateSchema = z.object({
  /** All turns in the conversation */
  turns: z.array(ConversationTurnSchema),
  /** Number of interactions (assistant messages sent) */
  interactionCount: z.number(),
  /** Fields that are still missing */
  missingFields: z.array(MissingFieldSchema),
  /** Data extracted from the conversation */
  extractedData: z.record(z.string(), z.unknown()),
  currentStage: z.nativeEnum(ConversationStage),
  isComplete: z.boolean(),
  /** When the conversation started */
  startedAt: z.date(),
  /** Last activity timestamp */
  lastActivityAt: z.date(),
});

export type ConversationState = z.infer<typeof ConversationStateSchema>;

// =============================================================================
// Conversation Limits (Anti-loop protection)
// =============================================================================

export const ConversationLimitsSchema = z.object({
  /** Maximum number of assistant interactions before escalation */
  maxInteractions: z.number().default(5),
  /** Hours before conversation times out */
  timeoutHours: z.number().default(24),
  /** Maximum turns total (user + assistant) */
  maxTurns: z.number().default(20),
});

export type ConversationLimits = z.infer<typeof ConversationLimitsSchema>;

export const DEFAULT_CONVERSATION_LIMITS: ConversationLimits = {
  maxInteractions: 5,
  timeoutHours: 24,
  maxTurns: 20,
};

// =============================================================================
// Email Reply Input/Output
// =============================================================================

export interface EmailReplyInput {
  /** Original message ID to reply to */
  originalMessageId: string;
  /** Tenant ID */
  tenantId: string;
  /** Current conversation stage */
  stage: ConversationStage;
  /** Type of message being sent */
  messageType: MessageType;
  /** Message content (used as body; required as fallback when using Resend template) */
  content: string;
  /** Optional metadata */
  metadata?: Record<string, unknown>;
  /** Resend template use case (e.g. BILL_CREATION_MISSING_DATA). When set with templateResolver, sends via template. */
  templateUseCase?: string;
  /** Template variables for Resend. Used when templateUseCase is set and template alias is resolved. */
  templateVariables?: Record<string, string | number | boolean>;
}

export interface EmailReplyResult {
  success: boolean;
  sentMessageId?: string;
  error?: string;
  /** Recorded in the conversation history but never handed to the provider (why, e.g. the inbox does not reply to senders). */
  deliverySkipReason?: string;
}

// =============================================================================
// Triage Conversation Types
// =============================================================================

export interface TriageConversationInput {
  subject: string;
  bodyText: string;
  attachments: {
    filename: string;
    mimeType: string;
    size: number;
  }[];
  fromAddress: string;
}

export interface TriageConversationOutput {
  /** Whether the email is eligible for AP processing */
  isEligibleForAP: boolean;
  /** Confidence score 0-1 */
  confidence: number;
  /** Reasoning for the decision */
  reasoning: string;
  /** Detected document type */
  documentType?: 'INVOICE' | 'BOLETO' | 'NFE' | 'RECEIPT' | 'OTHER';
  /** Acknowledgment message to send to user */
  acknowledgeMessage?: string;
  /** If not eligible, rejection message */
  rejectionMessage?: string;
}

export interface ExtractionNotificationInput {
  /** Email message ID that originated this document */
  emailMessageId: string;
  /** Tenant ID */
  tenantId: string;
  /** Document ID being processed */
  documentId: string;
  /** Current notification stage */
  stage: ExtractionNotificationStage;
  /** Fields that are missing (for MISSING_INFO stage) */
  missingFields?: MissingField[];
  /** Error details (for ERROR stage) */
  error?: ProcessingError;
  /** Extracted data summary (for COMPLETED stage) */
  extractedSummary?: Record<string, unknown>;
}

export interface BillSummary {
  billId: string;
  vendorName: string;
  amount: number;
  /** YYYY-MM-DD — document due date on the payment (`ap_payment.document_due_date`). */
  documentDueDateYmd?: string;
  /** YYYY-MM-DD — scheduled payment date (`ap_payment.scheduled_date`). */
  scheduledDateYmd?: string;
  billNumber?: string;
}

export interface BillNotificationInput {
  /** Email message ID that originated this bill */
  emailMessageId: string;
  /** Tenant ID */
  tenantId: string;
  /** Document ID */
  documentId: string;
  /** Result of bill creation */
  result: BillNotificationResult;
  /** Bill summary (for SUCCESS) */
  billSummary?: BillSummary;
  /** Error details (for ERROR) */
  error?: ProcessingError;
  /** Reason for manual review (for NEEDS_REVIEW) */
  reviewReason?: string;
}

// =============================================================================
// Pending Conversation State (for continuation after user reply)
// =============================================================================

/** Processing stages that can request information */
export const AwaitingStage = {
  TRIAGE: 'TRIAGE',
  EXTRACTION: 'EXTRACTION',
  ASSIGNMENT: 'ASSIGNMENT',
  PASSWORD: 'PASSWORD',
} as const;

export type AwaitingStage = (typeof AwaitingStage)[keyof typeof AwaitingStage];

/**
 * State saved when system asks user for information
 * Stored in EmailMessage.conversationHistory
 */
export interface PendingConversationState {
  /** Type marker for identification */
  type: 'PENDING_INFO';
  /** Stage that requested the information */
  stage: AwaitingStage;
  /** Fields being requested */
  awaitingFields: MissingField[];
  /** Document ID if applicable */
  documentId?: string;
  /** Email message ID */
  messageId: string;
  /** When the request was made */
  askedAt: string; // ISO date string
  /** Number of clarification attempts */
  interactionCount: number;
  /** When the pending request was resolved and should no longer match future replies */
  resolvedAt?: string;
  /** Queue to re-enqueue after receiving reply */
  resumeQueue?: string;
  /** Additional context needed to resume processing */
  resumeContext?: Record<string, unknown>;
}

/**
 * Context when processing a user reply
 */
export interface ConversationReplyContext {
  /** Original email message that started the conversation */
  originalMessageId: string;
  /** The reply email message */
  replyMessageId: string;
  /** Pending state from the original message */
  pendingState: PendingConversationState;
  /** Content of the user's reply */
  replyContent: string;
  /** Attachments in the reply */
  replyAttachments: {
    filename: string;
    mimeType: string;
    size: number;
    blobPath?: string;
  }[];
  /**
   * Thread root snapshot for richer agent context (original request + history entries).
   */
  threadRoot?: {
    subject?: string | null;
    bodyPlain?: string | null;
    conversationHistory?: unknown;
  };
}

/**
 * Result of extracting information from user reply
 */
export interface ReplyExtractionResult {
  /** Whether extraction was successful */
  success: boolean;
  /** Extracted field values (fieldType -> value) */
  extractedValues: Record<string, unknown>;
  /** Fields still missing after extraction */
  stillMissing: MissingField[];
  /** New attachments that can be processed */
  newAttachments?: {
    filename: string;
    mimeType: string;
    blobPath: string;
  }[];
  /** If user provided unclear response */
  needsClarification?: boolean;
  /** Follow-up question if needed */
  followUpQuestion?: string;
}
